import React from 'react';
import {Box, Grid, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import NationalDashboardShell from '../components/NationalDashboard.comp';
import PlanGate from '../components/PlanGate.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, rowsFrom, kpiText, addInfoOf} from '../../DashboardCommon/dashboardCards';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';
import {NAVY, TEAL, PageHeader, headCell, bodyCell} from '../components/widgets.comp';

const BLUE = '#3B6FA0';

/* Slide 42 — Program Deployment (National). Live data (ND.S_12), in this page's own layout:
     Provider-created  C_2 Active Programs · C_3 Schools Assigned · C_4 Program Completion
     Teacher-created   C_6 · C_7 · C_8
     Program list      C_10 — Program · Creator · Target Area · Schools · Enrolled · Completed ·
                       Completion Rate · Status (National is one country: no Countries column)
   The KPI sub-lines are the cards' own add-infos (Deployed / Created, Students completed /
   enrolled). "Priority Curriculum Coverage" is not drawn — removed from the datahouse output
   by D-10. Every column of the program list sorts and filters from its header (QA 9 Oct 2026). */
const whole = (v) => (v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v)) ? String(Math.trunc(Number(v))) : null);
const pair = (card, a, aLabel, b, bLabel) => {
    const x = whole(addInfoOf(card, a)), y = whole(addInfoOf(card, b));
    if (x === null && y === null) return undefined;
    return [x !== null ? `${aLabel} ${x}` : null, y !== null ? `${bLabel} ${y}` : null].filter(Boolean).join(' · ');
};
const statsFor = (active, schools, completion) => [
    {label: 'Active Programs', value: kpiText(active), sub: pair(active, 'deployed', 'Deployed', 'created', 'Created') || 'deployed / created', accent: TEAL},
    {label: 'Schools Assigned', value: kpiText(schools), accent: BLUE},
    {label: 'Program Completion', value: kpiText(completion, true), sub: pair(completion, 'students completed', 'Completed', 'students enrolled', 'Enrolled'), accent: BLUE},
];

const StatCardAccent = ({label, value, sub, accent}) => (
    <Box sx={{
        display: 'flex', border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '10px',
        backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        overflow: 'hidden', height: '100%',
    }}>
        <Box sx={{width: '5px', flexShrink: 0, backgroundColor: accent}}/>
        <Box sx={{p: 2, flex: 1}}>
            <Typography sx={{
                fontSize: '10.5px', fontWeight: 700, color: '#6A7282', letterSpacing: '0.6px',
                textTransform: 'uppercase', mb: 1,
            }}>
                {label}
            </Typography>
            <Typography sx={{fontSize: '26px', fontWeight: 800, color: NAVY, lineHeight: 1}}>
                {value}
            </Typography>
            {sub && <Typography sx={{fontSize: '11px', color: '#9CA3AF', mt: 0.75}}>{sub}</Typography>}
        </Box>
    </Box>
);

const DeploymentStats = ({stats}) => (
    <Grid container spacing={2.5} sx={{mb: 4}}>
        {stats.map((s) => (
            <Grid item xs={12} sm={6} md={4} key={s.label}>
                <StatCardAccent {...s}/>
            </Grid>
        ))}
    </Grid>
);

const rateColor = (rate) => {
    const num = parseFloat(rate);
    if (isNaN(num)) return '#374151';
    return num >= 75 ? TEAL : '#E8730C';
};

const PROGRAM_COLUMNS = [
    {key: 'program', label: 'Program', type: 'text', align: 'left'},
    {key: 'creator', label: 'Program Creator', type: 'text', align: 'left'},
    {key: 'targetArea', label: 'Target Area', type: 'text', align: 'left'},
    {key: 'schools', label: 'Schools', type: 'number'},
    {key: 'enrolled', label: 'Students Enrolled', type: 'number'},
    {key: 'completed', label: 'Students Completed', type: 'number'},
    {key: 'rate', label: 'Completion Rate', type: 'percent'},
    {key: 'status', label: 'Status', type: 'band'},
];

// The table sits in its own component so the sort/filter hook runs below the page's loading branches.
const ProgramsTable = ({rows}) => {
    const t = useTableControls(rows, PROGRAM_COLUMNS);
    return (
        <>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={{
                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'auto',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
                <Table sx={{minWidth: 900}}>
                    <TableHead>
                        <TableRow>
                            {PROGRAM_COLUMNS.map((c, i) => (
                                <SortFilterHeaderCell key={c.key} {...t.headerProps(c.key)} sx={{...headCell, textAlign: c.align || 'center', ...(i === 0 ? {pl: 2.5} : {})}}/>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} sx={{...bodyCell, textAlign: 'center', py: 4, color: '#6A7282'}}>No programs created or deployed yet.</TableCell>
                            </TableRow>
                        )}
                        {rows.length > 0 && t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} sx={{...bodyCell, textAlign: 'center', py: 4, color: '#6A7282'}}>No program matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((row, i) => (
                            <TableRow key={`${row.program}-${rows.indexOf(row)}`} sx={{backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'}}>
                                <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.program}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.creator}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.targetArea}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.schools}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.enrolled}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.completed}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center', fontWeight: 700, color: rateColor(row.rate)}}>{row.rate}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.status}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const PROGRAM_DEPLOYMENT_SECTION_CODE = 'S_12';
const CARD_SECTIONS = [PROGRAM_DEPLOYMENT_SECTION_CODE];

const ProgramDeploymentPage = () => {
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });
    const card = (c) => findCard(dashboardData, PROGRAM_DEPLOYMENT_SECTION_CODE, c);
    const rows = rowsFrom(card('C_10'), {
        program: 'Program', creator: 'Program Creator', targetArea: 'Target Area',
        schools: ['Schools', 'Classes'], enrolled: 'Students Enrolled', completed: 'Students Completed',
        rate: ['Completion Rate %', 'Completion Rate'], status: 'Status',
    });

    if (loading) {
        return (
            <NationalDashboardShell>
                <CircularLoader />
            </NationalDashboardShell>
        );
    }

    if (!dashboardData) {
        return (
            <NationalDashboardShell>
                <NoDataAvailable />
            </NationalDashboardShell>
        );
    }

    return (
        <NationalDashboardShell>
            <PageHeader
                title="Program Deployment"
                subtitle="Are recommended programs actually being created, assigned, completed, and assessed?"
            />

            <Typography sx={{fontSize: '17px', fontWeight: 700, color: NAVY, mb: 1.5}}>
                Provider-Created Programs
            </Typography>
            <DeploymentStats stats={statsFor(card('C_2'), card('C_3'), card('C_4'))}/>

            <Typography sx={{fontSize: '17px', fontWeight: 700, color: NAVY, mb: 1.5}}>
                Teacher-Created Programs
            </Typography>
            <DeploymentStats stats={statsFor(card('C_6'), card('C_7'), card('C_8'))}/>

            <Typography sx={{fontSize: '17px', fontWeight: 700, color: NAVY, mb: 1.5}}>
                List of Total Programs and Deployment Status
            </Typography>
            <ProgramsTable rows={rows}/>
        </NationalDashboardShell>
    );
};

// P1 + LC (dashboard catalog). Gated outside the page so a redirected user never fetches S_12.
const GatedProgramDeploymentPage = () => <PlanGate packages={['P1', 'LC']}><ProgramDeploymentPage/></PlanGate>;

export default GatedProgramDeploymentPage;
